import { IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { PaginatedDataGrid, StackedTextCell } from '@retail/components';
import { SupplierItemInsight } from '@retail/products/types';
import { useMemo } from 'react';
import { useRowColorGrouping } from '@retail/hooks';
import { InsightReportChips } from './InsightReportChips';
import { Pagination } from '@shared/hooks';
import { ReadMore } from '@mui/icons-material';
import { useAppTFunction } from '@retail/app/i18n';

type GridRowItem = GridColDef<SupplierItemInsight>;

const baseColumnProps: Partial<GridRowItem> = {
  width: 240,
  headerAlign: 'left',
  align: 'left',
};

interface Props {
  insightItems: SupplierItemInsight[];
  isFetching: boolean;
  hasActiveFilters: boolean;
  includeNotIncludedItemData: boolean;
  pagination: Pagination;
  totalItems?: number;
  setItemForProductCardDialog: (mgItemNumber?: number) => void;
}

export function InsightReportTable({
  insightItems,
  isFetching,
  hasActiveFilters,
  includeNotIncludedItemData,
  pagination,
  totalItems,
  setItemForProductCardDialog,
}: Props) {
  const t = useAppTFunction();
  const theme = useTheme();
  const { tint } = theme.vars.palette.semantic;
  const { getRowColorByIdGroups } = useRowColorGrouping();

  const mgItemNumbers = insightItems.map((item) => item.mgItemNumber);

  const columns: GridRowItem[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'mgItem/finfo',
        width: 140,
        headerName: t('products.mgItem.number.mgItemAndFinfo').toUpperCase(),

        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.mgItemNumber} bottomLine={row.finfoNumber} />
        ),
      },
      {
        ...baseColumnProps,
        field: 'itemText',
        width: 220,
        headerName: t('products.mgItem.details.primaryText').toUpperCase(),
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.primaryText} bottomLine={row.secondaryText} />
        ),
      },
      {
        ...baseColumnProps,
        field: 'supplier',
        width: 200,
        headerName: t('products.supplier.name').toUpperCase(),
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.supplier.name} bottomLine={row.supplier.mgSupplierId} />
        ),
      },
      {
        ...baseColumnProps,
        field: 'environment',
        headerName: t('products.mgItem.tabs.environment').toUpperCase(),
        renderCell: ({ row }) =>
          row.environment && (
            <InsightReportChips
              values={row.environment}
              activeColor={{ bg: tint.green.medium, text: tint.green.text }}
              includeNotIncludedItemData={includeNotIncludedItemData}
            />
          ),
      },
      {
        ...baseColumnProps,
        field: 'documentation',
        headerName: t('products.mgItem.documentation.title').toUpperCase(),
        renderCell: ({ row }) =>
          row.documentation ? (
            <InsightReportChips
              values={row.documentation}
              activeColor={{ bg: tint.sky.medium, text: tint.sky.text }}
              includeNotIncludedItemData={includeNotIncludedItemData}
            />
          ) : (
            '-'
          ),
      },
      {
        ...baseColumnProps,
        field: 'climateData',
        headerName: t('products.mgItem.climateData').toUpperCase(),
        renderCell: ({ row }) => {
          const climateData = row.climate?.gwp;
          const source = climateData?.a1a3FromEpd ? 'EPD' : 'BOVERKET';

          const total =
            source === 'EPD'
              ? (climateData?.a1a3FromEpd ?? 0) +
                (climateData?.a4FromEpd ?? 0) +
                (climateData?.a5FromEpd ?? 0) +
                (climateData?.c1c4 ?? 0) +
                (climateData?.d ?? 0)
              : (climateData?.a1a3FromBoverket ?? 0) +
                (climateData?.a4FromBoverket ?? 0) +
                (climateData?.a5FromBoverket ?? 0);
          return (
            <Stack display="grid" gridTemplateColumns="1.2fr 0.8fr">
              <Stack gap={0.5}>
                <Typography variant="body2">A1-A3:</Typography>
                <Typography variant="body2">A4:</Typography>
                <Typography variant="body2">A5:</Typography>
                <Typography variant="body2" flexWrap="wrap" color="textPrimary">
                  {t('products.mgItem.gwp.total')}:
                </Typography>
              </Stack>
              <Stack alignItems="flex-end" gap={0.5}>
                <Typography variant="body2" color="textPrimary">
                  {source === 'EPD'
                    ? climateData?.a1a3FromEpd
                    : climateData?.a1a3FromBoverket ?? '-'}
                </Typography>

                <Typography variant="body2" color="textPrimary">
                  {source === 'EPD' ? climateData?.a4FromEpd : climateData?.a4FromBoverket ?? '-'}
                </Typography>

                <Typography variant="body2" color="textPrimary">
                  {source === 'EPD' ? climateData?.a5FromEpd : climateData?.a5FromBoverket ?? '-'}
                </Typography>

                <Typography variant="subtitle2" color="textPrimary">
                  {total === 0 ? '-' : total.toFixed(2)}
                </Typography>
              </Stack>
            </Stack>
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'etimClass',
        width: 100,
        headerName: t('products.mgItem.etim.title').toUpperCase(),
        renderCell: ({ row }) => row.etimKl ?? '-',
      },
      {
        ...baseColumnProps,
        field: 'picture',
        headerName: t('products.mgItem.pictureData.pictures').toUpperCase(),
        renderCell: ({ row }) =>
          row.pictureData && (
            <InsightReportChips
              values={row.pictureData}
              activeColor={{ bg: tint.sky.strong, text: tint.sky.text }}
              includeNotIncludedItemData={includeNotIncludedItemData}
            />
          ),
      },
      {
        ...baseColumnProps,
        field: 'actions',
        headerName: '',
        width: 74,
        renderCell: ({ row }) => (
          <Tooltip title={t('myAssortment.actions.seeProductInfo')}>
            <IconButton onClick={() => setItemForProductCardDialog(row.mgItemNumber)}>
              <ReadMore />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [includeNotIncludedItemData, tint, setItemForProductCardDialog, t]
  );

  return (
    <PaginatedDataGrid
      columns={columns}
      loading={isFetching}
      getRowId={(row) => `${row.mgItemNumber}${row.finfoNumber}`}
      rows={insightItems}
      getRowHeight={() => 'auto'}
      gridPagination={pagination}
      paginationMode="server"
      rowCount={totalItems ?? 0}
      sx={{ '& .MuiDataGrid-cell': { p: 2 } }}
      getRowClassName={(value) =>
        getRowColorByIdGroups(
          mgItemNumbers,
          value.row.mgItemNumber,
          value.indexRelativeToCurrentPage
        )
      }
      disableRowSelectionOnClick
      disableColumnMenu
      disableColumnReorder
      disableColumnSorting
      initialState={{ pinnedColumns: { right: ['actions'] } }}
      slots={{
        noRowsOverlay: () => (
          <Stack alignItems="center" justifyContent="center" gap={2} height="100%">
            {hasActiveFilters ? (
              <Typography variant="h4" color="textSecondary">
                {t('products.search.noResults')}
              </Typography>
            ) : (
              <Typography variant="h4" color="textSecondary">
                {t('products.mgItem.search.infoText')}
              </Typography>
            )}
          </Stack>
        ),
      }}
    />
  );
}
